import {Injectable, DestroyRef} from '@angular/core';
import {AlertModalService} from '../../common/component/modal';
import {SpatialUtilService} from './spatial-util.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {catchError, EMPTY, of, switchMap, tap, throwError} from 'rxjs';
import {AnnotationService} from './features/annotation.service';
import {FeaturesAdjustmentUtilService} from './features-adjustment-util.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class FeaturesAdjustmentService {
    title = 'FeaturesAdjustmentService';

    constructor(
        private annotationService: AnnotationService,
        private alertModalService: AlertModalService,
        private featuresAdjustmentUtilService: FeaturesAdjustmentUtilService,
        private spatialUtilService: SpatialUtilService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {
        this.init();
    }

    init() {
        this.eventBus
            .on('adjustFeatures')
            .pipe(
                switchMap((meta: MetaData) => {
                    const {mapMode, mapScale, scaleRatio} = meta.data;

                    const allPoints = this.spatialUtilService.getPoints();

                    return this.adjustAnglesFeatures(scaleRatio, allPoints).pipe(
                        switchMap(() => this.adjustAnnotationsFeatures(mapScale, mapMode, scaleRatio, allPoints)),
                        switchMap(() =>
                            this.featuresAdjustmentUtilService.adjustLotOrRoadFeatures(this.spatialUtilService.getAnnotations(), mapScale, scaleRatio)
                        )
                    );
                }),
                tap(() => this.spatialUtilService.unhighlightAllGeometryFeatures()),
                takeUntilDestroyed(this.destroyRef),
                catchError(error => {
                    console.log(`${this.title} - on - adjustFeatures - error:`, error);
                    this.alertModalService.createAlertMessage('Error adjusting features', 'Ok');
                    return EMPTY;
                })
            )
            .subscribe();
    }

    private adjustAnglesFeatures(scaleRatio: any, allPoints: any) {
        console.debug(`${this.title} - adjustAnglesFeatures`);

        const allAngles = this.spatialUtilService.getAngles();
        const features = [];

        allAngles.forEach(feature => {
            const featureId = Number(feature.attributes.pointid);
            const pointFeature = allPoints.find(p => p.attributes.id === featureId);

            if (pointFeature) {
                const pointGeometry = pointFeature.geometry;

                if (pointGeometry !== undefined && !isNaN(pointGeometry.x) && !isNaN(pointGeometry.y)) {
                    const distance = Math.sqrt(Math.pow(feature.geometry.x - pointGeometry.x, 2) + Math.pow(feature.geometry.y - pointGeometry.y, 2));
                    const bearing = Math.atan2(feature.geometry.y - pointGeometry.y, feature.geometry.x - pointGeometry.x);
                    const updatedX = pointGeometry.x + distance * scaleRatio * Math.cos(bearing);
                    const updatedY = pointGeometry.y + distance * scaleRatio * Math.sin(bearing);

                    if (!isNaN(updatedX) && !isNaN(updatedY) && (feature.geometry.x !== updatedX || feature.geometry.y !== updatedY)) {
                        feature.geometry.x = updatedX;
                        feature.geometry.y = updatedY;
                        feature.attributes.x = feature.geometry.x;
                        feature.attributes.y = feature.geometry.y;

                        features.push(feature);
                    }
                } else {
                    console.error('Point feature id: ' + pointFeature.attributes.id + ' has undefined geometry!');
                }
            } else {
                console.debug(this.title + ' : adjustAnglesFeatures : point not found for featureId ' + featureId);
            }
        });

        if (features.length > 0) {
            return this.spatialUtilService.applyEdits('angle', null, features, null).pipe(
                tap(res => {
                    console.debug(`${this.title} - adjustAnglesFeatures - updates:`, res.updates);
                    this.spatialUtilService.updateMapGraphicGeometries(this.spatialUtilService.getAngleMapLayer(), features);
                }),
                catchError(err => {
                    console.error(`${this.title} - adjustAnglesFeatures - error updating features:`, err);
                    return throwError(() => err);
                })
            );
        }

        return of(null);
    }

    private adjustAnnotationsFeatures(mapScale: any, mapMode: any, scaleRatio: any, allPoints: any) {
        console.debug(`${this.title} : adjustAnnotationsFeatures`);

        const allAnnotations = this.spatialUtilService.getAnnotations();
        const allConstructPoints = this.spatialUtilService.getConstructPoints();
        const allLines = this.spatialUtilService.getLines();
        const allConstructLines = this.spatialUtilService.getConstructLines();

        const pointAnnotationFeatures = this.getAdjustedPointAnnotationFeatures(allAnnotations, allPoints, allConstructPoints, mapScale, scaleRatio);
        const lineAnnotationFeatures = this.getAdjustedLineAnnotationFeatures(allAnnotations, allLines, allConstructLines, mapScale, scaleRatio);

        const annotationsToBeAdjusted = [...pointAnnotationFeatures, ...lineAnnotationFeatures];

        if (annotationsToBeAdjusted.length > 0) {
            return this.spatialUtilService.applyEdits('annotation', null, annotationsToBeAdjusted, null).pipe(
                tap(res => {
                    console.debug(`${this.title} : adjustAnnotationsFeatures - updates:`, res.updates);
                    this.spatialUtilService.updateMapGraphicGeometries(this.spatialUtilService.getAnnotationMapLayer(), annotationsToBeAdjusted);
                }),
                catchError(() => {
                    return throwError(() => {
                        console.error(`${this.title}: Error moving features`);
                        new Error();
                    });
                })
            );
        }

        return of(null);
    }

    getAdjustedPointAnnotationFeatures(allAnnotations: any, allPoints: any, allConstructPoints: any, refMapScale: any, scaleRatio: any) {
        console.debug(this.title + ' : getAdjustedPointAnnotationFeatures');

        const output = [];

        allAnnotations.forEach(annotation => {
            let feature: object;

            if (annotation.attributes.featuretype === 'point') {
                feature = allPoints.find(point => point.attributes.id === annotation.attributes.featureid);
            } else if (annotation.attributes.featuretype === 'constructPoint') {
                feature = allConstructPoints.find(point => point.attributes.id === annotation.attributes.featureid);
            }

            if (feature) {
                this.adjustPointAnnotation(annotation, feature, scaleRatio, output);
            }
        });

        return output;
    }

    getAdjustedLineAnnotationFeatures(allAnnotations: any, allLines: any, allConstructLines: any, refMapScale: any, scaleRatio: any) {
        const output = [];

        allAnnotations.forEach(annotation => {
            let feature;

            if (annotation.attributes.featuretype === 'line' || ['line', 'polyline'].includes(annotation.attributes.childtype)) {
                const featureId = annotation.attributes.featuretype === 'line' ? annotation.attributes.featureid : annotation.attributes.childid;
                feature = allLines.find(line => line.attributes.id === featureId);
            } else if (annotation.attributes.featuretype === 'constructLine' || annotation.attributes.childtype === 'constructLine') {
                const featureId =
                    annotation.attributes.featuretype === 'constructLine' ? annotation.attributes.featureid : annotation.attributes.childid;
                feature = allConstructLines.find(line => line.attributes.id === featureId);
            }
            if (feature) {
                this.adjustLineAnnotation(annotation, feature, scaleRatio, output);
            }
        });

        return output;
    }

    getAdjustedPointFeature(feature: any, pointGeometry: any, scaleRatio: any) {
        if (pointGeometry && !isNaN(pointGeometry.x) && !isNaN(pointGeometry.y)) {
            const distance = Math.sqrt(Math.pow(feature.geometry.x - pointGeometry.x, 2) + Math.pow(feature.geometry.y - pointGeometry.y, 2));
            const bearing = Math.atan2(feature.geometry.y - pointGeometry.y, feature.geometry.x - pointGeometry.x);
            const updatedX = pointGeometry.x + distance * scaleRatio * Math.cos(bearing);
            const updatedY = pointGeometry.y + distance * scaleRatio * Math.sin(bearing);

            if (!isNaN(updatedX) && !isNaN(updatedY) && (feature.geometry.x !== updatedX || feature.geometry.y !== updatedY)) {
                feature.geometry.x = updatedX;
                feature.geometry.y = updatedY;
                feature.attributes.x = feature.geometry.x;
                feature.attributes.y = feature.geometry.y;
                return feature;
            }
        } else {
            console.error('Point feature id: ' + feature.attributes.id + ' has undefined geometry!');
        }

        return null;
    }

    getAdjustedLineFeature(feature: any, lineGeometry: any, scaleRatio: any) {
        if (lineGeometry) {
            let path = lineGeometry.paths[0];
            if (path.length > 2) {
                const pathLengthInfo = this.annotationService.getPathLengthInfo(path);

                path = path.slice(pathLengthInfo.middleSegmentIndex, pathLengthInfo.middleSegmentIndex + 2);
            }

            if (path && path[0] && path[1] && path[0][0] && path[0][1] && path[1][0] && path[1][1]) {
                const lineRadians = Math.atan2(path[1][1] - path[0][1], path[1][0] - path[0][0]);
                const featureRadians = Math.atan2(feature.geometry.y - path[0][1], feature.geometry.x - path[0][0]);
                const hypotenuseDistance = Math.sqrt(Math.pow(feature.geometry.x - path[0][0], 2) + Math.pow(feature.geometry.y - path[0][1], 2));
                const angle = Math.atan2(Math.sin(featureRadians - lineRadians), Math.cos(featureRadians - lineRadians));
                const intersectDistance = hypotenuseDistance * Math.cos(angle);
                const intersectX = path[0][0] + intersectDistance * Math.cos(lineRadians);
                const intersectY = path[0][1] + intersectDistance * Math.sin(lineRadians);
                const intersectRadians = Math.atan2(feature.geometry.y - intersectY, feature.geometry.x - intersectX);
                const distanceFromLine = hypotenuseDistance * Math.sin(angle);

                const updatedX = intersectX + distanceFromLine * scaleRatio * Math.cos(intersectRadians) * (angle < 0 ? -1 : 1);
                const updatedY = intersectY + distanceFromLine * scaleRatio * Math.sin(intersectRadians) * (angle < 0 ? -1 : 1);

                if (!isNaN(updatedX) && !isNaN(updatedY) && (feature.geometry.x !== updatedX || feature.geometry.y !== updatedY)) {
                    feature.geometry.x = updatedX;
                    feature.geometry.y = updatedY;
                    feature.attributes.x = feature.geometry.x;
                    feature.attributes.y = feature.geometry.y;
                    return feature;
                }
            } else {
                console.error('Line feature id: ' + feature.attributes.featureid + ' has undefined geometry!');
            }
        }
        return null;
    }

    private adjustPointAnnotation(annotation: any, feature: any, scaleRatio: any, output: any) {
        console.debug(`${this.title} : adjustPointAnnotation`);

        const adjustedAnnotation = this.getAdjustedPointFeature(annotation, feature.geometry, scaleRatio);

        if (adjustedAnnotation) {
            output.push(adjustedAnnotation);
        }
    }

    private adjustLineAnnotation(annotation: any, feature: any, scaleRatio: any, output: any) {
        console.debug(`${this.title} : adjustLineAnnotation`);

        const adjustedAnnotation = this.getAdjustedLineFeature(annotation, feature.geometry, scaleRatio);

        if (adjustedAnnotation) {
            output.push(adjustedAnnotation);
        }
    }
}
